import { Entypo, Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { Divider, Spin, Text } from '_/components';
import Button from '_/components/Button';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { appConfig } from '_/config/app';
import { colors, fontFamily } from '_/config/theme';
import themes from '_/constants/themes';
import { AppRoute, SigninProps } from '_/routes/types';
import { signInRequest, signInWithGoogleRequest } from '_/store/modules/auth/actions';
import * as AuthSession from 'expo-auth-session/providers/google';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as LocalAuthentication from 'expo-local-authentication';
import * as SecureStore from 'expo-secure-store';
import {
  View,
  Image,
  Platform,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  StyleSheet,
  Alert,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { showMessage } from 'react-native-flash-message';
import FloatingInput from '_/components/FloatingInput';
import OfflineQRCodesModal from '_/components/OfflineQRCodesModal';
import { transparentize } from 'polished';

export default function Signin() {
  const navigation = useNavigation<SigninProps>();
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const reduxEmail = useSelector((state: any) => state?.auth?.email);

  const [email, setEmail] = useState(reduxEmail || '');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);

  const [userEmailLocal, setUserEmailLocal] = useState<string | null>(null);
  const [userPasswordLocal, setUserPasswordLocal] = useState<string | null>(null);

  const [qrModalVisible, setQrModalVisible] = useState(false);

  const [biometricsStatus, setBiometricsStatus] = useState<
    'unsupported' | 'not_enrolled' | 'available' | 'enabled'
  >('unsupported');

  const { loading } = useSelector((state: any) => state.loading);

  const [request, response, promptAsync] = AuthSession.useAuthRequest({
    clientId: appConfig.googleLoginConfig.googleClientId,
    androidClientId: appConfig.googleLoginConfig.androidClientId,
    iosClientId: appConfig.googleLoginConfig.iosClientId,
  });

  const checkBiometricsStatus = async () => {
    const hasHardware = await LocalAuthentication.hasHardwareAsync();

    if (!hasHardware) {
      setBiometricsStatus('unsupported');

      return;
    }

    const isEnrolled = await LocalAuthentication.isEnrolledAsync();

    if (!isEnrolled) {
      setBiometricsStatus('not_enrolled');

      return;
    }

    const savedEmail = await SecureStore.getItemAsync('user_email');
    const savedPassword = await SecureStore.getItemAsync('user_password');

    if (savedEmail && savedPassword) {
      setUserEmailLocal(savedEmail);
      setUserPasswordLocal(savedPassword);
      setBiometricsStatus('enabled');

      return;
    }

    setBiometricsStatus('available');
    await SecureStore.setItemAsync('biometrics_status', 'available');
  };

  useEffect(() => {
    checkBiometricsStatus();
  }, []);

  useEffect(() => {
    if (response?.type === 'success' && response.authentication?.accessToken) {
      signInWithGoogleAsync(response.authentication?.accessToken);
    }
  }, [response]);

  const passwordRef = useRef<TextInput>(null);

  async function saveEmailPassword(email: string, password: string) {
    try {
      await SecureStore.setItemAsync('user_email', email);
      await SecureStore.setItemAsync('user_password', password);
    } catch (error) {
      console.error('Erro ao salvar email:', error);
    }
  }

  async function maybeAskForBiometrics(email: string, password: string) {
    await saveEmailPassword(email, password);

    if (biometricsStatus !== 'available') return;

    Alert.alert(
      translate('biometrics.title'),
      `${translate('biometrics.menuDescriptionDisabled')}\n\n${translate(
        'biometrics.securityNotice'
      )}`,
      [
        {
          text: translate('notNow'),
          style: 'cancel',
        },
        {
          text: translate('enable'),
          onPress: async () => {
            await SecureStore.setItemAsync('biometrics_status', 'enabled');
          },
        },
      ]
    );
  }

  const handleBiometricPress = () => {
    switch (biometricsStatus) {
      case 'unsupported':
        Alert.alert(
          translate('biometrics.notSupportedTitle'),
          translate('biometrics.notSupportedMessage')
        );
        break;

      case 'not_enrolled':
        Alert.alert(
          translate('biometrics.notEnrolledTitle'),
          translate('biometrics.notEnrolledMessage')
        );
        break;

      case 'available':
        Alert.alert(
          translate('biometrics.notEnabledTitle'),
          translate('biometrics.notEnabledMessage')
        );
        break;

      case 'enabled':
        authenticateLocally();
        break;
    }
  };

  function togglePasswordVisibility() {
    setPasswordVisible(!passwordVisible);
  }

  async function handleSubmitLogin() {
    const formattedEmail = email.trim().toLowerCase();

    if (formattedEmail && password) {
      dispatch(
        signInRequest({
          email: formattedEmail,
          password,
          onSuccess: () => {
            maybeAskForBiometrics(email, password);
          },
        })
      );
    }
  }

  async function signInWithGoogleAsync(token: string) {
    dispatch(signInWithGoogleRequest(token));
  }

  const authenticateLocally = async () => {
    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: 'Confirm your identity',
      fallbackLabel: 'Use device PIN',
      cancelLabel: 'Cancel',
      disableDeviceFallback: false,
    });

    const formattedEmail = userEmailLocal?.trim()?.toLowerCase();
    if (result.success && formattedEmail && userPasswordLocal) {
      dispatch(signInRequest({ email: formattedEmail, password: userPasswordLocal }));
    } else {
      showMessage({
        message: translate('authSaga.authSignInError'),
        type: 'danger',
      });
    }
  };

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <Spin visible={loading} />

        <Image source={themes.images.buildings} style={styles.imageContainer} resizeMode="cover" />

        <View style={styles.overlay} />
        <View style={styles.content}>
          <KeyboardAvoidingView
            enabled
            style={styles.formContainer}
            behavior={Platform.OS === 'ios' ? 'position' : undefined}
          >
            <Image style={styles.logo} source={themes.images.logo} resizeMode="contain" />

            <FloatingInput
              label={translate('signin.email')}
              value={email}
              onChangeText={setEmail}
              returnKeyType="next"
              autoCapitalize="none"
              keyboardType="email-address"
              textContentType="emailAddress"
            />

            <FloatingInput
              label={translate('signin.password')}
              returnKeyType="go"
              autoCapitalize="none"
              textContentType="password"
              value={password}
              ref={passwordRef}
              autoCorrect={false}
              onSubmitEditing={handleSubmitLogin}
              secureTextEntry={!passwordVisible}
              onChangeText={setPassword}
              rightContent={
                <TouchableOpacity
                  activeOpacity={0.8}
                  onPress={togglePasswordVisibility}
                  hitSlop={{ top: 20, left: 20, bottom: 20, right: 20 }}
                >
                  <Feather name={passwordVisible ? 'eye' : 'eye-off'} size={20} color="#999" />
                </TouchableOpacity>
              }
            />

            <TouchableOpacity
              style={styles.btnForgotPassword}
              onPress={() =>
                navigation.navigate(AppRoute.FORGOT_PASSWORD, {
                  defaultEmail: reduxEmail,
                })
              }
            >
              <Text uppercase={false} style={styles.btnForgotPasswordText}>
                {translate('signin.forgotPassword')}
              </Text>
            </TouchableOpacity>

            <Button
              buttonStyles={{ marginTop: 24 }}
              onPress={handleSubmitLogin}
              leftIcon="mail"
              leftIconStyles={styles.iconMail}
            >
              <Text uppercase={false} style={[styles.btnText, styles.btnMailText]}>
                {translate('signin.signInEmail')}
              </Text>
            </Button>
          </KeyboardAvoidingView>

          <Divider
            containerStyles={{ marginVertical: 12 }}
            color={themes.colors.grayPrimary}
            borderColor={themes.colors.lightTertiary}
          >
            {translate('signin.or')}
          </Divider>

          <View style={styles.quickActionsContainer}>
            <TouchableOpacity
              style={styles.quickActionItem}
              disabled={!request}
              onPress={() => promptAsync()}
              activeOpacity={0.8}
            >
              <View style={styles.iconBox}>
                <Image
                  source={themes.images.googleIcon}
                  style={styles.quickIconImage}
                  resizeMode="contain"
                />
              </View>
              <Text color={themes.colors.grayPrimary} fontType="medium" size={12}>
                {translate('Google')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.quickActionItem}
              onPress={handleBiometricPress}
              activeOpacity={0.8}
            >
              <View style={styles.iconBox}>
                <Entypo name="fingerprint" size={24} color={colors.secondary} />
              </View>
              <Text color={themes.colors.grayPrimary} fontType="medium" size={12}>
                {translate('biometrics.label')}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.bottomSection}>
            {/* <TouchableOpacity
              style={styles.qrCard}
              activeOpacity={0.85}
              onPress={() => setQrModalVisible(true)}
            >
              <View style={styles.qrIconContainer}>
                <AntDesign name="qrcode" size={22} color="#FFF" />
              </View>

              <Text
                size={16}
                fontType="medium"
                color={colors.white}
                style={{ flex: 1, textAlign: 'center', marginRight: 40 }}
              >
                Meus QR Codes
              </Text>
            </TouchableOpacity> */}

            <TouchableOpacity
              style={styles.btnSignUp}
              onPress={() => navigation.navigate(AppRoute.SIGNOUT)}
            >
              <Text style={styles.btnSignUpText}>
                {translate('signin.newUser')}{' '}
                <Text style={{ fontFamily: fontFamily.medium }}>{translate('signin.signUp')}</Text>
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
      <OfflineQRCodesModal
        visible={qrModalVisible}
        onClose={() => setQrModalVisible(false)}
        qrcodesOfflines={[]}
        onGoToProfile={() => {
          setQrModalVisible(false);
        }}
      />
    </ContainerWidthLimit>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: themes.colors.white,
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 24,
    marginTop: 68,
  },
  imageContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    // opacity: 0.15,
  },
  overlay: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: transparentize(0.15, '#ffffff'),
  },
  logo: {
    alignSelf: 'center',
    width: 160,
    height: 80,
    marginVertical: 28,
    marginBottom: 80,
  },
  loginImage: {
    width: '100%',
    height: 110,
  },
  formContainer: {
    // marginTop: 28,
    justifyContent: 'center',
    // backgroundColor: 'red',
  },
  btnForgotPassword: {
    alignSelf: 'flex-end',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  btnForgotPasswordText: {
    color: themes.colors.grayPrimary,
    fontFamily: fontFamily.medium,
    fontSize: 12,
  },
  btnText: {
    textAlign: 'center',
    fontFamily: fontFamily.medium,
    flex: 1,
    fontSize: 18,
  },
  btnSignUp: {
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
  },
  btnSignUpText: {
    color: themes.colors.darkSecondary,
    alignSelf: 'center',
    fontFamily: fontFamily.light,
    fontSize: 18,
  },
  iconMail: {
    fontSize: 18,
    color: themes.colors.white,
  },
  iconsInput: {
    fontSize: 18,
    color: themes.colors.lightSecondary,
  },

  btnMailText: {
    color: themes.colors.white,
  },
  quickActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 42,
    // backgroundColor: 'green',
  },
  quickActionItem: {
    alignItems: 'center',
    gap: 4,
  },
  iconBox: {
    width: 64,
    height: 64,
    borderRadius: 18,
    backgroundColor: themes.colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: themes.colors.lightTertiary,
    shadowColor: '#000',
    shadowOpacity: 0.03,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 2,
  },
  quickIconImage: {
    width: 24,
    height: 24,
  },
  qrCard: {
    backgroundColor: themes.colors.primary,
    borderRadius: 24,
    paddingVertical: 20,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 6 },
    shadowRadius: 10,
    elevation: 6,
    marginBottom: 8,
  },
  qrIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  bottomSection: {
    marginTop: 'auto',
    gap: 20,
    // backgroundColor: 'blue',
  },
});
